/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { View, TouchableOpacity, Platform, Keyboard, Text } from 'react-native';
import {
  pd,
  getColor,
  textStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import BottomSheetView, {
  BottomSheetRefs,
} from '@airasia-phoenix/react-native/components/BottomSheetView';
import SelectionFieldView, {
  SelectionFieldRefs,
} from '@airasia-phoenix/react-native/components/SelectionFieldView';
import SearchableListPageView, {
  SectionListItem,
  ViewType,
} from '@airasia-phoenix/react-native/components/SearchableListPageView';
import { Alignment } from '@airasia-phoenix/react-native/components/SearchableListPageView/ListEmptyComponent';
import { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import {
  autoCapitalizeValues,
  TextFieldRefs,
} from '@airasia-phoenix/react-native/components/TextFieldView/types';
import { StationTypeFilter, useStationsMap } from '@airasia-rn/purchases';
import { useDeviceInfo } from '@airasia-rn/ui';
import { useNearestAirportsCodes } from '@airasia-rn/flights/hooks';
import { useTranslation } from 'react-i18next';
import { useLogger } from '@airasia-rn/core-logger';
import type {
  SearchStationsViewHandle,
  SearchStationsViewProps,
  Station,
  StationSelectionPayload,
  StationSelectionSource,
} from '../../types';
import { useDestinations } from '../../hooks/snap/search';
import { useSnapSearchViewFeatureFlag } from '../../hooks/useSnapSearchViewFeatureFlag';
import { useSnapRecentSearches } from '../../hooks/useSnapRecentSearches';
import { useStationListData } from '../../hooks/useStationListData';
import {
  ACTIONS,
  SNAP_SEARCH_WIDGET,
} from '../../analytics/snapSearchWidget/snapSearchWidget.meta';
import {
  buildSearchWidgetEventAnalyticsNative,
  buildSearchWidgetEventAnalyticsWeb,
  type AnalyticsParams,
} from '../../analytics/snapSearchWidget/snapSearchWidget.analytics';
import {
  DROP_DOWN_PORTAL_HOST_NAME,
  MAX_RECENT_SEARCHES,
  SNAP_SEARCH_WIDGET_LABELS,
  SNAP_SEARCH_WIDGET_TEST_IDS,
  STATION,
  STATION_SELECTION_SOURCE,
} from '../../configs/constants';
import {
  StationListModel,
  getStationDisplayValue,
} from '../../utils/stationSearchListUtils';
import {
  filterStationsForSnap,
  findNearestSnapOrigin,
  mapPurchasesStationToSnap,
  StationSearchIndex,
} from '../../utils/stationUtils';
import { styles } from './styles';

type StationSelection = {
  station: Station;
  isAllAirports: boolean;
  selectionSource?: StationSelectionSource;
};
type DesktopActiveField = (typeof STATION)[keyof typeof STATION] | null;

const DROP_DOWN_WIDTH_MOBILE = 360;
const DROP_DOWN_WIDTH_DESKTOP = 360;
const DROP_DOWN_HEIGHT_DEFAULT_WEB = 392;
const DROP_DOWN_HEIGHT_NO_RESULT = 392;
const SEARCH_QUERY_MIN_LENGTH = 3;
const ORIGIN_LIST_ID_PREFIX = `${STATION.ORIGIN}-`;
const DESTINATION_ORIGIN_FALLBACK_KEY = STATION.ORIGIN;
const BOTTOM_SHEET_HEIGHT = {
  initialHeight: '80%',
  finalHeight: '80%',
  height: '100%',
} as const;

const SearchStationsView = forwardRef<
  SearchStationsViewHandle,
  SearchStationsViewProps
>(
  (
    {
      onSelect,
      isLoading = false,
      testID = SNAP_SEARCH_WIDGET_TEST_IDS.ROOT,
      showSearchButton = true,
      originStation = null,
      destinationStation = null,
      errors = {},
    },
    ref,
  ) => {
    const { t } = useTranslation('snap');
    const { logEvent } = useLogger();
    const originFieldRef = useRef<SelectionFieldRefs>(null);
    const destinationFieldRef = useRef<SelectionFieldRefs>(null);
    const originSheetRef = useRef<BottomSheetRefs>(null);
    const destinationSheetRef = useRef<BottomSheetRefs>(null);
    const originMobileInputRef = useRef<TextFieldRefs>(null);
    const destinationMobileInputRef = useRef<TextFieldRefs>(null);
    const stationFocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const isOriginSheetOpenRef = useRef(false);
    const isDestinationSheetOpenRef = useRef(false);
    const pendingMobileWebFocusRef = useRef<
      typeof STATION.ORIGIN | typeof STATION.DESTINATION | null
    >(null);
    const didPrefillStationsRef = useRef(false);
    // Code of the airport the nearest-airport prefill put in From, until the
    // user picks or clears From themselves.
    const nearestPrefillCodeRef = useRef<string | null>(null);
    const lastNoResultsRef = useRef<{ origin: string; destination: string }>({
      origin: '',
      destination: '',
    });

    const { isWeb, isMobileWidth } = useDeviceInfo();
    const isMobileWeb = isWeb && isMobileWidth;
    const isDesktopWeb = isWeb && !isMobileWidth;

    const [originSelection, setOriginSelection] =
      useState<StationSelection | null>(
        originStation ? { station: originStation, isAllAirports: false } : null,
      );
    const [destinationSelection, setDestinationSelection] =
      useState<StationSelection | null>(
        destinationStation
          ? { station: destinationStation, isAllAirports: false }
          : null,
      );
    const [originQuery, setOriginQuery] = useState('');
    const [destinationQuery, setDestinationQuery] = useState('');
    const [activeDesktopField, setActiveDesktopField] =
      useState<DesktopActiveField>(null);

    useEffect(() => {
      setOriginSelection(
        originStation ? { station: originStation, isAllAirports: false } : null,
      );
      setOriginQuery('');
    }, [originStation]);

    useEffect(() => {
      setDestinationSelection(
        destinationStation
          ? { station: destinationStation, isAllAirports: false }
          : null,
      );
      setDestinationQuery('');
    }, [destinationStation]);

    const { stationsMap, isStationsLoading } = useStationsMap(
      StationTypeFilter.ALL,
    );
    const stations = useMemo(() => {
      const mapped = Object.values(stationsMap ?? {}).map((station) =>
        mapPurchasesStationToSnap(station),
      );
      return filterStationsForSnap(mapped);
    }, [stationsMap]);
    const searchIndex = useMemo(() => {
      if (!stations.length) return null;
      return new StationSearchIndex(stations);
    }, [stations]);
    const isLoadingStations = isStationsLoading;
    const { recentSearchesStore } = useSnapRecentSearches();
    const recentSearches = useMemo(
      () => recentSearchesStore?.recentSearches ?? [],
      [recentSearchesStore?.recentSearches],
    );
    const { data: validDestinations } = useDestinations(
      originSelection?.station.StationCode ?? '',
    );
    // Flights' query, shared with the homepage. Only the prefill effect uses it,
    // and only when nothing else decides From.
    const { data: nearestAirports } = useNearestAirportsCodes();
    const nearestStation = useMemo(
      () => findNearestSnapOrigin(nearestAirports?.airport ?? [], stations),
      [nearestAirports?.airport, stations],
    );

    const recentOriginSearches = useMemo(() => {
      const seen = new Set<string>();
      const result: { station: Station; isAllAirports: boolean }[] = [];
      for (const entry of recentSearches) {
        if (!seen.has(entry.from.StationCode)) {
          seen.add(entry.from.StationCode);
          result.push({
            station: entry.from,
            isAllAirports: entry.fromIsAllAirports ?? false,
          });
          if (result.length >= MAX_RECENT_SEARCHES) break;
        }
      }
      return result;
    }, [recentSearches]);

    const recentDestinationSearches = useMemo(() => {
      const originCode = originSelection?.station?.StationCode;
      if (!originCode) return [];
      const isOriginInRecentOrigins = recentOriginSearches.some(
        (entry) => entry.station.StationCode === originCode,
      );
      if (!isOriginInRecentOrigins) return [];
      const seen = new Set<string>();
      const result: { station: Station; isAllAirports: boolean }[] = [];
      for (const entry of recentSearches) {
        if (
          entry.from.StationCode === originCode &&
          !seen.has(entry.to.StationCode)
        ) {
          seen.add(entry.to.StationCode);
          result.push({
            station: entry.to,
            isAllAirports: entry.toIsAllAirports ?? false,
          });
          if (result.length >= MAX_RECENT_SEARCHES) break;
        }
      }
      return result;
    }, [
      recentSearches,
      recentOriginSearches,
      originSelection?.station?.StationCode,
    ]);

    useEffect(() => {
      // The nearest airport is only a provisional From: recent searches are
      // restored from storage after first render, so they can arrive after it
      // was applied. Until the user touches the form, a recent search that
      // shows up replaces it. Tracked by airport code, not selectionSource,
      // because the originStation prop sync rewrites the selection without one.
      const isUntouchedNearestPrefill =
        nearestPrefillCodeRef.current !== null &&
        originSelection?.station.StationCode ===
          nearestPrefillCodeRef.current &&
        !destinationSelection;
      if (didPrefillStationsRef.current && !isUntouchedNearestPrefill) return;
      if (
        !isUntouchedNearestPrefill &&
        (originSelection || destinationSelection)
      ) {
        didPrefillStationsRef.current = true;
        return;
      }
      if (originQuery || destinationQuery) return;

      // Recent searches come first: when the user has any, the latest one is
      // the default, never the nearest airport.
      const latest = recentSearches[0];
      if (latest) {
        nearestPrefillCodeRef.current = null;
        setOriginSelection({
          station: latest.from,
          isAllAirports: latest.fromIsAllAirports ?? false,
          selectionSource: STATION_SELECTION_SOURCE.RECENT,
        });
        setDestinationSelection({
          station: latest.to,
          isAllAirports: latest.toIsAllAirports ?? false,
          selectionSource: STATION_SELECTION_SOURCE.RECENT,
        });
        // Keep parent form state in sync with the auto-prefilled recent search.
        onSelect?.({
          from: latest.from,
          to: latest.to,
          fromIsAllAirports: latest.fromIsAllAirports ?? false,
          toIsAllAirports: latest.toIsAllAirports ?? false,
        });
        didPrefillStationsRef.current = true;
        return;
      }

      // No recent search (yet): the nearest airport fills From. To stays empty.
      // Already applied, so nothing more to do until a recent search arrives.
      if (isUntouchedNearestPrefill || !nearestStation) return;

      nearestPrefillCodeRef.current = nearestStation.StationCode;
      setOriginSelection({
        station: nearestStation,
        isAllAirports: false,
        selectionSource: STATION_SELECTION_SOURCE.NEAREST,
      });
      onSelect?.({
        from: nearestStation,
        to: null,
        fromIsAllAirports: false,
        toIsAllAirports: false,
      });
      didPrefillStationsRef.current = true;
    }, [
      recentSearches,
      nearestStation,
      originSelection,
      destinationSelection,
      originQuery,
      destinationQuery,
      onSelect,
    ]);

    const trimmedOriginQuery = originQuery.trim();
    const trimmedDestinationQuery = destinationQuery.trim();
    const isSwapDisabled = !originSelection || !destinationSelection;

    const originListData = useStationListData({
      query: originQuery,
      stations,
      searchIndex,
      recentStations: recentOriginSearches,
      idPrefix: ORIGIN_LIST_ID_PREFIX,
      t,
    });

    const destinationListData = useStationListData({
      query: destinationQuery,
      stations,
      searchIndex,
      recentStations: recentDestinationSearches,
      validDestinationCodes:
        trimmedDestinationQuery.length > 0 ? validDestinations : undefined,
      idPrefix: `${STATION.DESTINATION}-${originSelection?.station?.StationCode ?? DESTINATION_ORIGIN_FALLBACK_KEY}-`,
      t,
    });

    const flattenListData = useCallback(
      (
        data: SectionListItem[] | MemoizedListViewProps[],
      ): MemoizedListViewProps[] => {
        if (data.length === 0) return [];
        const firstItem = data[0] as SectionListItem | MemoizedListViewProps;
        if ('data' in firstItem && Array.isArray(firstItem.data)) {
          return (data as SectionListItem[]).flatMap((section) => section.data);
        }
        return data as MemoizedListViewProps[];
      },
      [],
    );

    const originDropdownData = useMemo(
      () =>
        trimmedOriginQuery.length > 0
          ? flattenListData(originListData)
          : originListData,
      [flattenListData, originListData, trimmedOriginQuery.length],
    );
    const destinationDropdownData = useMemo(
      () =>
        trimmedDestinationQuery.length > 0
          ? flattenListData(destinationListData)
          : destinationListData,
      [flattenListData, destinationListData, trimmedDestinationQuery.length],
    );
    const isOriginDesktopActive =
      !isDesktopWeb || activeDesktopField === STATION.ORIGIN;
    const isDestinationDesktopActive =
      !isDesktopWeb || activeDesktopField === STATION.DESTINATION;
    const visibleOriginDropdownData = useMemo(
      () => (isOriginDesktopActive ? originDropdownData : []),
      [isOriginDesktopActive, originDropdownData],
    );
    const visibleDestinationDropdownData = useMemo(
      () => (isDestinationDesktopActive ? destinationDropdownData : []),
      [isDestinationDesktopActive, destinationDropdownData],
    );
    const isOriginSearching =
      trimmedOriginQuery.length >= SEARCH_QUERY_MIN_LENGTH;
    const isDestinationSearching =
      trimmedDestinationQuery.length >= SEARCH_QUERY_MIN_LENGTH;
    const originNoResults =
      !isLoadingStations &&
      isOriginSearching &&
      visibleOriginDropdownData.length === 0;
    const destinationNoResults =
      !isLoadingStations &&
      isDestinationSearching &&
      visibleDestinationDropdownData.length === 0;

    useEffect(() => {
      if (!trimmedOriginQuery) {
        lastNoResultsRef.current.origin = '';
        return;
      }
      if (!originNoResults) return;
      if (lastNoResultsRef.current.origin === trimmedOriginQuery) return;

      const analyticsParams: AnalyticsParams | null =
        Platform.select({
          native: buildSearchWidgetEventAnalyticsNative(ACTIONS.TAP, {
            label: SNAP_SEARCH_WIDGET_LABELS.NO_RESULTS,
            value: `${STATION.ORIGIN}${SNAP_SEARCH_WIDGET.VALUE_SEPARATOR}${trimmedOriginQuery}`,
          }),
          default: buildSearchWidgetEventAnalyticsWeb(ACTIONS.TAP, {
            label: `${SNAP_SEARCH_WIDGET_LABELS.NO_RESULTS}${SNAP_SEARCH_WIDGET.VALUE_SEPARATOR}${STATION.ORIGIN}${SNAP_SEARCH_WIDGET.VALUE_SEPARATOR}${trimmedOriginQuery}`,
          }),
        }) ?? null;
      if (analyticsParams) {
        logEvent(analyticsParams.eventName, analyticsParams.eventParams);
      }
      lastNoResultsRef.current.origin = trimmedOriginQuery;
    }, [originNoResults, trimmedOriginQuery, logEvent]);

    useEffect(() => {
      if (!trimmedDestinationQuery) {
        lastNoResultsRef.current.destination = '';
        return;
      }
      if (!destinationNoResults) return;
      if (lastNoResultsRef.current.destination === trimmedDestinationQuery)
        return;

      const analyticsParams: AnalyticsParams | null =
        Platform.select({
          native: buildSearchWidgetEventAnalyticsNative(ACTIONS.TAP, {
            label: SNAP_SEARCH_WIDGET_LABELS.NO_RESULTS,
            value: `${STATION.DESTINATION}${SNAP_SEARCH_WIDGET.VALUE_SEPARATOR}${trimmedDestinationQuery}`,
          }),
          default: buildSearchWidgetEventAnalyticsWeb(ACTIONS.TAP, {
            label: `${SNAP_SEARCH_WIDGET_LABELS.NO_RESULTS}${SNAP_SEARCH_WIDGET.VALUE_SEPARATOR}${STATION.DESTINATION}${SNAP_SEARCH_WIDGET.VALUE_SEPARATOR}${trimmedDestinationQuery}`,
            //value: `${STATION.DESTINATION}${SNAP_SEARCH_WIDGET.VALUE_SEPARATOR}${trimmedDestinationQuery}`,
          }),
        }) ?? null;
      if (analyticsParams) {
        logEvent(analyticsParams.eventName, analyticsParams.eventParams);
      }

      lastNoResultsRef.current.destination = trimmedDestinationQuery;
    }, [destinationNoResults, trimmedDestinationQuery, logEvent]);
    const handleOriginSelect = useCallback(
      (selectedItems?: MemoizedListViewProps[]) => {
        const performSelect = () => {
          // The user is choosing From themselves: the nearest prefill is final.
          nearestPrefillCodeRef.current = null;
          const selected = selectedItems?.[0];
          if (!selected) {
            setOriginSelection(null);
            setDestinationSelection(null);
            setOriginQuery('');
            setDestinationQuery('');
            setActiveDesktopField(null);
            onSelect?.({
              from: null,
              to: null,
              fromIsAllAirports: false,
              toIsAllAirports: false,
            });
            return;
          }

          const { station, isAllAirports, selectionSource } =
            selected.model as StationListModel;
          setOriginSelection({
            station,
            isAllAirports,
            selectionSource:
              selectionSource ?? STATION_SELECTION_SOURCE.GENERAL,
          });
          setDestinationSelection(null);
          setOriginQuery('');
          setDestinationQuery('');
          setActiveDesktopField(null);

          // Notify parent of selection change
          onSelect?.({
            from: station,
            to: null,
            fromIsAllAirports: isAllAirports,
            toIsAllAirports: false,
          });

          if (isDesktopWeb) return;

          if (pendingMobileWebFocusRef.current === STATION.ORIGIN) {
            pendingMobileWebFocusRef.current = null;
          }
          if (stationFocusTimeoutRef.current) {
            clearTimeout(stationFocusTimeoutRef.current);
            stationFocusTimeoutRef.current = null;
          }
          isOriginSheetOpenRef.current = false;
          originSheetRef.current?.toggleBottomSheetView();
        };

        if (process.env.NODE_ENV === 'test') {
          performSelect();
        } else {
          Promise.resolve().then(performSelect);
        }
      },
      [isDesktopWeb, onSelect],
    );

    const handleDestinationSelect = useCallback(
      (selectedItems?: MemoizedListViewProps[]) => {
        const performSelect = () => {
          const selected = selectedItems?.[0];
          if (!selected) {
            setDestinationSelection(null);
            setDestinationQuery('');
            setActiveDesktopField(null);
            onSelect?.({
              from: originSelection?.station ?? null,
              to: null,
              fromIsAllAirports: originSelection?.isAllAirports ?? false,
              toIsAllAirports: false,
            });
            return;
          }

          const { station, isAllAirports, selectionSource } =
            selected.model as StationListModel;
          const originCode = originSelection?.station.StationCode;
          const originSource =
            originSelection?.selectionSource ??
            STATION_SELECTION_SOURCE.GENERAL;
          if (originCode) {
            const destinationSource =
              selectionSource ?? STATION_SELECTION_SOURCE.GENERAL;
            const analyticsParams: AnalyticsParams | null =
              Platform.select({
                native: buildSearchWidgetEventAnalyticsNative(ACTIONS.TAP, {
                  label: `${originCode}-${station.StationCode}(${originSource}-${destinationSource})`,
                }),
                default: buildSearchWidgetEventAnalyticsWeb(ACTIONS.TAP, {
                  label: `${originCode}-${station.StationCode}(${originSource}-${destinationSource})`,
                }),
              }) ?? null;
            if (analyticsParams) {
              logEvent(analyticsParams.eventName, analyticsParams.eventParams);
            }
          }

          setDestinationSelection({
            station,
            isAllAirports,
            selectionSource:
              selectionSource ?? STATION_SELECTION_SOURCE.GENERAL,
          });
          setDestinationQuery('');
          setActiveDesktopField(null);

          if (originSelection?.station.StationCode === station.StationCode) {
            setOriginSelection(null);
            // Notify parent that origin was cleared
            onSelect?.({
              from: null,
              to: station,
              fromIsAllAirports: false,
              toIsAllAirports: isAllAirports,
            });
          } else {
            // Notify parent of selection change
            onSelect?.({
              from: originSelection?.station ?? null,
              to: station,
              fromIsAllAirports: originSelection?.isAllAirports ?? false,
              toIsAllAirports: isAllAirports,
            });
          }

          if (!isDesktopWeb) {
            if (pendingMobileWebFocusRef.current === STATION.DESTINATION) {
              pendingMobileWebFocusRef.current = null;
            }
            if (stationFocusTimeoutRef.current) {
              clearTimeout(stationFocusTimeoutRef.current);
              stationFocusTimeoutRef.current = null;
            }
            isDestinationSheetOpenRef.current = false;
            destinationSheetRef.current?.toggleBottomSheetView();
          }
        };

        if (process.env.NODE_ENV === 'test') {
          performSelect();
        } else {
          Promise.resolve().then(performSelect);
        }
      },
      [originSelection, isDesktopWeb, onSelect, logEvent],
    );

    const validateAndSearch = useCallback(() => {
      // Validation is now handled by parent (SearchFormView)
      // Just trigger the parent's onSelect with current selections
      const payload: StationSelectionPayload = {
        from: originSelection?.station ?? null,
        to: destinationSelection?.station ?? null,
        fromIsAllAirports: originSelection?.isAllAirports ?? false,
        toIsAllAirports: destinationSelection?.isAllAirports ?? false,
      };
      onSelect?.(payload);
    }, [originSelection, destinationSelection, onSelect]);

    useImperativeHandle(
      ref,
      () => ({
        submit: validateAndSearch,
      }),
      [validateAndSearch],
    );

    const handleOriginBottomSheetClose = useCallback(() => {
      Keyboard.dismiss();
      originFieldRef.current?.blurWithText();
      originFieldRef.current?.blur();
      setOriginQuery('');
    }, []);

    const handleDestinationBottomSheetClose = useCallback(() => {
      Keyboard.dismiss();
      destinationFieldRef.current?.blurWithText();
      destinationFieldRef.current?.blur();
      setDestinationQuery('');
    }, []);

    const handleOriginQueryChange = useCallback(
      (text: string) => {
        if (isDesktopWeb) {
          setActiveDesktopField(STATION.ORIGIN);
        }
        setOriginQuery(text);
      },
      [isDesktopWeb],
    );

    const handleDestinationQueryChange = useCallback(
      (text: string) => {
        if (isDesktopWeb) {
          setActiveDesktopField(STATION.DESTINATION);
        }
        setDestinationQuery(text);
      },
      [isDesktopWeb],
    );

    const handleOriginQueryClear = useCallback(() => {
      setOriginQuery('');
    }, []);

    const handleDestinationQueryClear = useCallback(() => {
      setDestinationQuery('');
    }, []);

    const clearStationFocusTimeout = useCallback(() => {
      if (stationFocusTimeoutRef.current) {
        clearTimeout(stationFocusTimeoutRef.current);
        stationFocusTimeoutRef.current = null;
      }
    }, []);

    const openOriginSheet = useCallback(() => {
      if (isOriginSheetOpenRef.current) {
        return;
      }

      isOriginSheetOpenRef.current = true;
      pendingMobileWebFocusRef.current = isMobileWeb ? STATION.ORIGIN : null;
      const toggleOriginSheet = () => {
        originSheetRef.current?.toggleBottomSheetView();
      };

      if (isMobileWeb) {
        requestAnimationFrame(toggleOriginSheet);
        return;
      }

      toggleOriginSheet();
    }, [isMobileWeb]);

    const openDestinationSheet = useCallback(() => {
      if (isDestinationSheetOpenRef.current) {
        return;
      }

      isDestinationSheetOpenRef.current = true;
      pendingMobileWebFocusRef.current = isMobileWeb
        ? STATION.DESTINATION
        : null;
      const toggleDestinationSheet = () => {
        destinationSheetRef.current?.toggleBottomSheetView();
      };

      if (isMobileWeb) {
        requestAnimationFrame(toggleDestinationSheet);
        return;
      }

      toggleDestinationSheet();
    }, [isMobileWeb]);

    const resetOriginMobileSheet = useCallback(() => {
      isOriginSheetOpenRef.current = false;
      if (pendingMobileWebFocusRef.current === STATION.ORIGIN) {
        pendingMobileWebFocusRef.current = null;
      }
      clearStationFocusTimeout();
      Keyboard.dismiss();
      setOriginQuery('');
    }, [clearStationFocusTimeout]);

    const resetDestinationMobileSheet = useCallback(() => {
      isDestinationSheetOpenRef.current = false;
      if (pendingMobileWebFocusRef.current === STATION.DESTINATION) {
        pendingMobileWebFocusRef.current = null;
      }
      clearStationFocusTimeout();
      Keyboard.dismiss();
      setDestinationQuery('');
    }, [clearStationFocusTimeout]);

    const handleOriginSheetChange = useCallback(
      (index: number) => {
        if (index < 0) {
          resetOriginMobileSheet();
          return;
        }

        if (
          !isMobileWeb ||
          pendingMobileWebFocusRef.current !== STATION.ORIGIN
        ) {
          return;
        }

        pendingMobileWebFocusRef.current = null;
        clearStationFocusTimeout();
        stationFocusTimeoutRef.current = setTimeout(() => {
          originMobileInputRef.current?.focus();
        }, 150);
      },
      [clearStationFocusTimeout, isMobileWeb, resetOriginMobileSheet],
    );

    const handleDestinationSheetChange = useCallback(
      (index: number) => {
        if (index < 0) {
          resetDestinationMobileSheet();
          return;
        }

        if (
          !isMobileWeb ||
          pendingMobileWebFocusRef.current !== STATION.DESTINATION
        ) {
          return;
        }

        pendingMobileWebFocusRef.current = null;
        clearStationFocusTimeout();
        stationFocusTimeoutRef.current = setTimeout(() => {
          destinationMobileInputRef.current?.focus();
        }, 150);
      },
      [clearStationFocusTimeout, isMobileWeb, resetDestinationMobileSheet],
    );

    const handleOriginSheetClose = useCallback(() => {
      resetOriginMobileSheet();
      originSheetRef.current?.toggleBottomSheetView();
    }, [resetOriginMobileSheet]);

    const handleDestinationSheetClose = useCallback(() => {
      resetDestinationMobileSheet();
      destinationSheetRef.current?.toggleBottomSheetView();
    }, [resetDestinationMobileSheet]);

    const handleOriginSheetCloseByBackdrop = resetOriginMobileSheet;

    const handleDestinationSheetCloseByBackdrop = resetDestinationMobileSheet;

    const baseHeaderTextStyle = useMemo(
      () => ({
        ...textStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
      }),
      [],
    );
    const recentHeaderTextStyle = useMemo(
      () => ({
        ...baseHeaderTextStyle,
        marginTop: pd.ref.space.spacing40.px,
        marginBottom: -10,
      }),
      [baseHeaderTextStyle],
    );
    const originHeaderTextStyle =
      trimmedOriginQuery.length === 0
        ? recentHeaderTextStyle
        : baseHeaderTextStyle;
    const destinationHeaderTextStyle =
      trimmedDestinationQuery.length === 0
        ? recentHeaderTextStyle
        : baseHeaderTextStyle;

    const originTextFieldViewProps = useMemo(
      () => ({
        autoFocus: true,
        selectTextOnFocus: isDesktopWeb,
        autoCorrect: false,
        autoCapitalize: autoCapitalizeValues.NONE,
        label: t('searchSnapPage.stationField.origin'),
        disabled: false,
        endIcon: {
          icon: pd.icon.main.navigation.cancel,
          height: pd.ref.space.spacing60.px,
          width: pd.ref.space.spacing60.px,
          tint: getColor(pd.sys.colour.content_primary),
        },
        onEndIconPress: handleOriginQueryClear,
      }),
      [handleOriginQueryClear, isDesktopWeb, t],
    );

    const destinationTextFieldViewProps = useMemo(
      () => ({
        autoFocus: true,
        selectTextOnFocus: isDesktopWeb,
        autoCorrect: false,
        autoCapitalize: autoCapitalizeValues.NONE,
        label: t('searchSnapPage.stationField.destination'),
        disabled: false,
        endIcon: {
          icon: pd.icon.main.navigation.cancel,
          height: pd.ref.space.spacing60.px,
          width: pd.ref.space.spacing60.px,
          tint: getColor(pd.sys.colour.content_primary),
        },
        onEndIconPress: handleDestinationQueryClear,
      }),
      [handleDestinationQueryClear, isDesktopWeb, t],
    );

    const dropDownWidth = isDesktopWeb
      ? DROP_DOWN_WIDTH_DESKTOP
      : DROP_DOWN_WIDTH_MOBILE;
    const noResultsTitle = t('searchSnapPage.noResultFound.title');
    const noResultsSubtitle = t('searchSnapPage.noResultFound.subtext');
    const noResultsSheetPrimaryTextStyle = useMemo(
      () => ({
        ...textStyle(
          pd.sys.typescale.body_text_medium,
          pd.sys.colour.content_primary,
        ),
        textAlign: 'center' as const,
        width: '100%' as const,
      }),
      [],
    );
    const noResultsSheetSecondaryTextStyle = useMemo(
      () => ({
        ...textStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
        textAlign: 'center' as const,
        width: '100%' as const,
      }),
      [],
    );

    const { snap_enable_portal_usage: isPortalUsageEnabled } =
      useSnapSearchViewFeatureFlag();

    const originSearchableListViewProps = useMemo(
      () => ({
        viewType: isPortalUsageEnabled
          ? ViewType.DropDownWithPortal
          : ViewType.DropDown,
        dropDownHeight: originNoResults
          ? DROP_DOWN_HEIGHT_NO_RESULT
          : DROP_DOWN_HEIGHT_DEFAULT_WEB,
        dropDownWidth,
        dropDownContainerStyle: originNoResults
          ? styles.noResultsDropdownContainer
          : originQuery.length > 0
            ? styles.stationResultsTopGap
            : styles.dropdownContainerTopGap,
        dropdownPortalHostName: DROP_DOWN_PORTAL_HOST_NAME,
        isTextFieldVisible: false,
        dropDownZIndex: 23,
        showItemSeparatorComponent: false,
        isLoading: isOriginDesktopActive ? isLoadingStations : false,
        startedSearching: isOriginDesktopActive ? isOriginSearching : false,
        headerTextStyle: originHeaderTextStyle,
        headerViewStyle: styles.searchHeaderPadding,
        textFieldViewProps: originTextFieldViewProps,
        searchableListPageViewData: visibleOriginDropdownData,
        onChangeText: handleOriginQueryChange,
        onChange: handleOriginSelect,
        listEmptyComponentProps: {
          contentStateViewProps: {
            primaryText: noResultsTitle,
            secondaryText: noResultsSubtitle,
          },
          alignment: Alignment.TOP,
          searchValue: originQuery,
          startedSearching: isOriginSearching,
        },
        paddingTop: isPortalUsageEnabled ? undefined : 54,
      }),
      [
        originHeaderTextStyle,
        noResultsTitle,
        noResultsSubtitle,
        visibleOriginDropdownData,
        originQuery,
        isLoadingStations,
        isOriginDesktopActive,
        handleOriginQueryChange,
        handleOriginSelect,
        originTextFieldViewProps,
        dropDownWidth,
        originNoResults,
        isOriginSearching,
        styles.noResultsDropdownContainer,
        styles.stationResultsTopGap,
        styles.dropdownContainerTopGap,
        styles.searchHeaderPadding,
      ],
    );

    const destinationSearchableListViewProps = useMemo(
      () => ({
        viewType: isPortalUsageEnabled
          ? ViewType.DropDownWithPortal
          : ViewType.DropDown,
        dropDownHeight: destinationNoResults
          ? DROP_DOWN_HEIGHT_NO_RESULT
          : DROP_DOWN_HEIGHT_DEFAULT_WEB,
        dropDownWidth,
        dropDownContainerStyle: destinationNoResults
          ? styles.noResultsDropdownContainer
          : destinationQuery.length > 0
            ? styles.stationResultsTopGap
            : styles.dropdownContainerTopGap,
        dropdownPortalHostName: DROP_DOWN_PORTAL_HOST_NAME,
        isTextFieldVisible: false,
        dropDownZIndex: 24,
        showItemSeparatorComponent: false,
        isLoading: isDestinationDesktopActive ? isLoadingStations : false,
        startedSearching: isDestinationDesktopActive
          ? isDestinationSearching
          : false,
        headerTextStyle: destinationHeaderTextStyle,
        headerViewStyle: styles.searchHeaderPadding,
        textFieldViewProps: destinationTextFieldViewProps,
        searchableListPageViewData: visibleDestinationDropdownData,
        onChangeText: handleDestinationQueryChange,
        onChange: handleDestinationSelect,
        listEmptyComponentProps: {
          contentStateViewProps: {
            primaryText: noResultsTitle,
            secondaryText: noResultsSubtitle,
          },
          alignment: Alignment.TOP,
          searchValue: destinationQuery,
          startedSearching: isDestinationSearching,
        },
        paddingTop: isPortalUsageEnabled ? undefined : 54,
      }),
      [
        destinationHeaderTextStyle,
        noResultsTitle,
        noResultsSubtitle,
        visibleDestinationDropdownData,
        destinationQuery,
        isLoadingStations,
        isDestinationDesktopActive,
        handleDestinationQueryChange,
        handleDestinationSelect,
        destinationTextFieldViewProps,
        dropDownWidth,
        destinationNoResults,
        isDestinationSearching,
        styles.noResultsDropdownContainer,
        styles.stationResultsTopGap,
        styles.dropdownContainerTopGap,
        styles.searchHeaderPadding,
      ],
    );

    const isLoadingState = isLoading || isLoadingStations;
    const disableInputsOnWeb = isDesktopWeb && isLoadingState;

    const originError = errors.origin;
    const destinationError = errors.destination;

    return (
      <View style={styles.container} testID={testID}>
        <View
          style={[
            styles.stationRowSection,
            !showSearchButton && styles.stationRowSectionNoButton,
          ]}
          testID={`${testID}-station-row-section`}
        >
          <View style={styles.stationRowWrapper}>
            <View style={styles.stationRow}>
              <View
                style={styles.fieldWrapper}
                onStartShouldSetResponderCapture={() => {
                  if (isDesktopWeb) {
                    setActiveDesktopField(STATION.ORIGIN);
                    destinationFieldRef.current?.blurWithText();
                    destinationFieldRef.current?.blur();
                  }
                  return false;
                }}
              >
                {isDesktopWeb ? (
                  <SelectionFieldView
                    ref={originFieldRef}
                    disabled={disableInputsOnWeb}
                    isError={Boolean(originError)}
                    errorMessage={originError}
                    endIcon={undefined}
                    label={t('searchSnapPage.stationField.origin')}
                    width="100%"
                    value={getStationDisplayValue(
                      originSelection?.station ?? null,
                      originSelection?.isAllAirports ?? false,
                      t,
                    )}
                    testID={`${testID}-from`}
                    searchableListViewProps={originSearchableListViewProps}
                    bottomSheetViewProps={{
                      ...BOTTOM_SHEET_HEIGHT,
                      children: undefined,
                      showHandle: false,
                      sectionTitle: '',
                      onBottomSheetClose: handleOriginBottomSheetClose,
                      onCloseIconPress: handleOriginBottomSheetClose,
                    }}
                  />
                ) : (
                  <TextFieldView
                    label={t('searchSnapPage.stationField.origin')}
                    value={getStationDisplayValue(
                      originSelection?.station ?? null,
                      originSelection?.isAllAirports ?? false,
                      t,
                    )}
                    selection={{ start: 0, end: 0 }}
                    disabled={false}
                    isError={Boolean(originError)}
                    errorMessage={originError}
                    width="100%"
                    editable={false}
                    onPress={openOriginSheet}
                    testID={`${testID}-from`}
                  />
                )}
              </View>
              <View style={styles.fieldSpacer} />

              <View
                style={styles.fieldWrapper}
                onStartShouldSetResponderCapture={() => {
                  if (isDesktopWeb) {
                    setActiveDesktopField(STATION.DESTINATION);
                    originFieldRef.current?.blurWithText();
                    originFieldRef.current?.blur();
                  }
                  return false;
                }}
              >
                {isDesktopWeb ? (
                  <SelectionFieldView
                    ref={destinationFieldRef}
                    disabled={disableInputsOnWeb}
                    isError={Boolean(destinationError)}
                    errorMessage={destinationError}
                    endIcon={undefined}
                    label={t('searchSnapPage.stationField.destination')}
                    width="100%"
                    value={getStationDisplayValue(
                      destinationSelection?.station ?? null,
                      destinationSelection?.isAllAirports ?? false,
                      t,
                    )}
                    testID={`${testID}-to`}
                    searchableListViewProps={destinationSearchableListViewProps}
                    bottomSheetViewProps={{
                      ...BOTTOM_SHEET_HEIGHT,
                      children: undefined,
                      showHandle: false,
                      sectionTitle: '',
                      onBottomSheetClose: handleDestinationBottomSheetClose,
                      onCloseIconPress: handleDestinationBottomSheetClose,
                    }}
                  />
                ) : (
                  <TextFieldView
                    label={t('searchSnapPage.stationField.destination')}
                    value={getStationDisplayValue(
                      destinationSelection?.station ?? null,
                      destinationSelection?.isAllAirports ?? false,
                      t,
                    )}
                    selection={{ start: 0, end: 0 }}
                    disabled={false}
                    isError={Boolean(destinationError)}
                    errorMessage={destinationError}
                    width="100%"
                    editable={false}
                    onPress={openDestinationSheet}
                    testID={`${testID}-to`}
                  />
                )}
              </View>

              <TouchableOpacity
                style={styles.swapOverlay}
                activeOpacity={1}
                disabled={isSwapDisabled}
                accessibilityState={{ disabled: isSwapDisabled }}
                onPress={() => {
                  if (!originSelection || !destinationSelection) return;

                  const tempOrigin = originSelection;
                  const tempDestination = destinationSelection;
                  setOriginSelection(tempDestination);
                  setDestinationSelection(tempOrigin);
                  setOriginQuery('');
                  setDestinationQuery('');
                  onSelect?.({
                    from: tempDestination.station,
                    to: tempOrigin.station,
                    fromIsAllAirports: tempDestination.isAllAirports,
                    toIsAllAirports: tempOrigin.isAllAirports,
                  });
                }}
                testID={`${testID}-swap`}
              >
                <View
                  style={[
                    styles.swapButton,
                    isSwapDisabled && styles.swapButtonDisabled,
                  ]}
                >
                  <IconView
                    icon={pd.icon.main.actions.swap_horiz}
                    height={pd.ref.space.spacing50.px}
                    width={pd.ref.space.spacing60.px}
                    tint={getColor(
                      isSwapDisabled
                        ? pd.sys.colour.outline_low
                        : pd.sys.colour.content_secondary,
                    )}
                  />
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {showSearchButton ? (
          <View style={styles.searchButtonContainer}>
            <ButtonView
              text={t('searchSnapPage.searchButton')}
              type={ButtonViewType.PRIMARY_REGULAR}
              disabled={false}
              onPress={validateAndSearch}
              testID={`${testID}-search-button`}
            />
          </View>
        ) : null}

        {!isDesktopWeb && (
          <>
            <BottomSheetView
              ref={originSheetRef}
              enableContentPanningGesture
              enablePanDownToClose={!isMobileWeb}
              enableOverDrag={false}
              showBackdrop
              backdropProps={{
                opacity: 1,
                backgroundColor: getColor(pd.sys.colour.overlay),
              }}
              showHandle={false}
              showHeader={false}
              sectionTitle=""
              {...BOTTOM_SHEET_HEIGHT}
              onChange={handleOriginSheetChange}
              onBottomSheetClose={handleOriginSheetCloseByBackdrop}
            >
              <View style={styles.sheetHeaderRow}>
                <TouchableOpacity
                  style={styles.sheetCloseButton}
                  onPress={handleOriginSheetClose}
                  testID={`${testID}-origin-sheet-close`}
                >
                  <IconView
                    icon={pd.icon.main.navigation.close}
                    height={24}
                    width={24}
                    tint={getColor(pd.sys.colour.content_primary)}
                  />
                </TouchableOpacity>
                <View style={styles.sheetInputWrapper}>
                  <TextFieldView
                    ref={originMobileInputRef}
                    label={t('searchSnapPage.stationField.origin')}
                    value={originQuery}
                    autoFocus={!isMobileWeb}
                    autoCorrect={false}
                    autoCapitalize={autoCapitalizeValues.NONE}
                    disabled={false}
                    onChangeText={handleOriginQueryChange}
                  />
                </View>
              </View>
              <View style={styles.sheetListContainer}>
                {originNoResults && !isLoadingStations ? (
                  <View style={styles.noResultsSheetContainer}>
                    <View style={styles.noResultsSheetTextContainer}>
                      <Text style={noResultsSheetPrimaryTextStyle}>
                        {noResultsTitle}
                      </Text>
                      <Text style={noResultsSheetSecondaryTextStyle}>
                        {noResultsSubtitle}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <SearchableListPageView
                    searchableListPageViewData={originListData}
                    showItemSeparatorComponent={false}
                    isLoading={isLoadingStations}
                    startedSearching={originQuery.length > 0}
                    listEmptyComponentProps={{
                      contentStateViewProps: {
                        primaryText: t('searchSnapPage.noResultFound.title'),
                        secondaryText: t(
                          'searchSnapPage.noResultFound.subtext',
                        ),
                      },
                      alignment: Alignment.TOP,
                      searchValue: originQuery,
                    }}
                    headerTextStyle={originHeaderTextStyle}
                    onChange={handleOriginSelect}
                    numColumns={1}
                    isTextFieldVisible={false}
                  />
                )}
              </View>
            </BottomSheetView>

            <BottomSheetView
              ref={destinationSheetRef}
              enableContentPanningGesture
              enablePanDownToClose={!isMobileWeb}
              enableOverDrag={false}
              showBackdrop
              backdropProps={{
                opacity: 1,
                backgroundColor: getColor(pd.sys.colour.overlay),
              }}
              showHandle={false}
              showHeader={false}
              sectionTitle=""
              {...BOTTOM_SHEET_HEIGHT}
              onChange={handleDestinationSheetChange}
              onBottomSheetClose={handleDestinationSheetCloseByBackdrop}
            >
              <View style={styles.sheetHeaderRow}>
                <TouchableOpacity
                  style={styles.sheetCloseButton}
                  onPress={handleDestinationSheetClose}
                  testID={`${testID}-destination-sheet-close`}
                >
                  <IconView
                    icon={pd.icon.main.navigation.close}
                    height={24}
                    width={24}
                    tint={getColor(pd.sys.colour.content_primary)}
                  />
                </TouchableOpacity>
                <View style={styles.sheetInputWrapper}>
                  <TextFieldView
                    ref={destinationMobileInputRef}
                    label={t('searchSnapPage.stationField.destination')}
                    value={destinationQuery}
                    autoFocus={!isMobileWeb}
                    autoCorrect={false}
                    autoCapitalize={autoCapitalizeValues.NONE}
                    disabled={false}
                    onChangeText={handleDestinationQueryChange}
                  />
                </View>
              </View>
              <View style={styles.sheetListContainer}>
                {destinationNoResults && !isLoadingStations ? (
                  <View style={styles.noResultsSheetContainer}>
                    <View style={styles.noResultsSheetTextContainer}>
                      <Text style={noResultsSheetPrimaryTextStyle}>
                        {noResultsTitle}
                      </Text>
                      <Text style={noResultsSheetSecondaryTextStyle}>
                        {noResultsSubtitle}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <SearchableListPageView
                    searchableListPageViewData={destinationListData}
                    showItemSeparatorComponent={false}
                    isLoading={isLoadingStations}
                    startedSearching={destinationQuery.length > 0}
                    listEmptyComponentProps={{
                      contentStateViewProps: {
                        primaryText: t('searchSnapPage.noResultFound.title'),
                        secondaryText: t(
                          'searchSnapPage.noResultFound.subtext',
                        ),
                      },
                      alignment: Alignment.TOP,
                      searchValue: destinationQuery,
                    }}
                    headerTextStyle={destinationHeaderTextStyle}
                    onChange={handleDestinationSelect}
                    numColumns={1}
                    isTextFieldVisible={false}
                  />
                )}
              </View>
            </BottomSheetView>
          </>
        )}
      </View>
    );
  },
);

SearchStationsView.displayName = 'SearchStationsView';

export default SearchStationsView;
